import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Lock, MoreVertical, Pin, Unlock, UserPlus, Users, ArrowRightLeft, Hand, X } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../services/axios';
import { usePermissions, permissionError } from '../../context/PermissionsContext';

const BLUE = '#0041C2';
const ACCESS_LABEL = { OWNER: 'Owner', EDITOR: 'Editor', VIEWER: 'Viewer' };

/** Small markers next to a position name: pinned, locked, my access. */
export function PositionBadges({ position }) {
  const items = [];
  if (position.pinnedGlobal) items.push({ key: 'pg', icon: <Pin size={11} />, text: 'Pinned', title: 'Pinned for everyone', color: BLUE });
  else if (position.pinnedByMe) items.push({ key: 'pm', icon: <Pin size={11} />, text: '', title: 'Pinned for you', color: '#6B7280' });
  if (position.locked) items.push({ key: 'lk', icon: <Lock size={11} />, text: 'Locked', title: `Locked${position.lockedBy ? ` by ${position.lockedBy}` : ''}: read-only except for managers and admins`, color: '#B45309' });
  if (position.myAccess === 'EDITOR' || position.myAccess === 'VIEWER') {
    items.push({ key: 'ac', icon: <Users size={11} />, text: ACCESS_LABEL[position.myAccess], title: `You are assigned as ${ACCESS_LABEL[position.myAccess]}`, color: position.myAccess === 'VIEWER' ? '#6B7280' : '#15803D' });
  }
  if (!items.length) return null;
  return (
    <span style={{ display: 'inline-flex', gap: 4, marginLeft: 6, flexShrink: 0 }}>
      {items.map((b) => (
        <span key={b.key} title={b.title} style={{
          display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 700,
          color: b.color, border: `1px solid ${b.color}40`, background: `${b.color}12`,
          borderRadius: 999, padding: '1px 6px', lineHeight: 1.5,
        }}>
          {b.icon}{b.text}
        </span>
      ))}
    </span>
  );
}

/** "⋮" menu on a position row: pin, lock, assign, transfer, pick up. */
export function PositionAccessMenu({ position, onChanged, onAssign, onTransfer }) {
  const perms = usePermissions();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const can = (key) => perms.canOn(position, key);
  const canPickUp = perms.policyOn('positions.open_pool') && perms.hasRole('RECRUITER')
    && !position.myAccess && (position.members || []).length === 0;

  const call = async (fn, ok) => {
    setOpen(false);
    try {
      await fn();
      if (ok) toast.success(ok);
      onChanged?.();
    } catch (err) {
      toast.error(permissionError(err, 'That did not work'));
    }
  };

  const items = [
    can('position.pin_self') && {
      key: 'pin-me', icon: <Pin size={13} />,
      label: position.pinnedByMe ? 'Unpin for me' : 'Pin for me',
      run: () => call(() => api.post(`/positions/${position.positionId}/pin`, { scope: 'me', pinned: !position.pinnedByMe }),
        position.pinnedByMe ? 'Unpinned' : 'Pinned for you'),
    },
    can('position.pin_all') && {
      key: 'pin-all', icon: <Pin size={13} color={BLUE} />,
      label: position.pinnedGlobal ? 'Unpin for everyone' : 'Pin for everyone',
      run: () => call(() => api.post(`/positions/${position.positionId}/pin`, { scope: 'all', pinned: !position.pinnedGlobal }),
        position.pinnedGlobal ? 'Unpinned for everyone' : 'Pinned for everyone'),
    },
    can('position.lock') && {
      key: 'lock', icon: position.locked ? <Unlock size={13} /> : <Lock size={13} />,
      label: position.locked ? 'Unlock position' : 'Lock position',
      run: () => call(() => api.post(`/positions/${position.positionId}/lock`, { locked: !position.locked }),
        position.locked ? 'Position unlocked' : 'Position locked'),
    },
    can('position.assign') && {
      key: 'assign', icon: <UserPlus size={13} />, label: 'Assign people…',
      run: () => { setOpen(false); onAssign?.(position); },
    },
    can('position.transfer') && {
      key: 'transfer', icon: <ArrowRightLeft size={13} />, label: 'Transfer ownership…',
      run: () => { setOpen(false); onTransfer?.(position); },
    },
    canPickUp && {
      key: 'pickup', icon: <Hand size={13} />, label: 'Pick up this position',
      run: () => call(() => api.post(`/positions/${position.positionId}/pick-up`), 'You are now an Editor on this position'),
    },
  ].filter(Boolean);

  if (!items.length) return null;

  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        className="pt-act"
        title="Pin, lock, assign"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <MoreVertical size={14} />
      </button>
      {open && (
        <div role="menu" style={{
          position: 'absolute', right: 0, top: 'calc(100% + 4px)', zIndex: 9999, minWidth: 210,
          background: 'var(--ds-card, #fff)', border: '1px solid var(--ds-border2, #D1D5DB)', borderRadius: 10,
          boxShadow: 'var(--ds-shadow-md, 0 8px 24px rgba(0,0,0,.15))', overflow: 'hidden', textAlign: 'left',
        }}>
          {items.map((it, i) => (
            <button
              key={it.key}
              type="button"
              role="menuitem"
              onClick={it.run}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '9px 14px',
                border: 'none', borderTop: i ? '1px solid var(--ds-border, #F3F4F6)' : 'none',
                background: 'transparent', cursor: 'pointer', fontSize: 12, color: 'var(--ds-text, #111827)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--ds-hover, #F3F4F6)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              {it.icon}{it.label}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

function Modal({ title, subtitle, onClose, children, footer }) {
  return (
    <div role="dialog" aria-modal="true" style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', zIndex: 10000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div style={{
        background: 'var(--ds-card, #fff)', color: 'var(--ds-text, #111827)', borderRadius: 12,
        width: '100%', maxWidth: 560, maxHeight: '85vh', display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ padding: '16px 18px 10px', display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{title}</div>
            {subtitle && <div style={{ fontSize: 12, color: 'var(--ds-muted, #6B7280)', marginTop: 3 }}>{subtitle}</div>}
          </div>
          <button type="button" aria-label="Close" onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--ds-muted, #6B7280)' }}>
            <X size={18} />
          </button>
        </div>
        <div style={{ padding: '0 18px', overflowY: 'auto' }}>{children}</div>
        <div style={{ padding: '12px 18px 16px', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>{footer}</div>
      </div>
    </div>
  );
}

const btn = (primary) => ({
  padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
  border: primary ? 'none' : '1px solid var(--ds-border2, #D1D5DB)',
  background: primary ? BLUE : 'transparent', color: primary ? '#fff' : 'var(--ds-text2, #374151)',
});

function useCompanyUsers() {
  const [users, setUsers] = useState(null);
  useEffect(() => {
    api.get('/positions/assignable-users')
      .then((res) => setUsers(Array.isArray(res.data) ? res.data : []))
      .catch(() => setUsers([]));
  }, []);
  return users;
}

const ROLE_SHORT = { ADMIN: 'Admin', MANAGER: 'Manager', RECRUITER: 'Recruiter', VIEWER: 'Viewer' };

/**
 * Assign people to a position. Each ticked person gets Editor (read + write) or Viewer (read only).
 * New ticks start at the "Default access for newly assigned people" rule.
 */
export function AssignPositionModal({ position, onClose, onSaved }) {
  const perms = usePermissions();
  const users = useCompanyUsers();
  const defaultAccess = perms.policy('assign.default_access') || 'EDITOR';
  const owner = (position.createdByEmail || '').toLowerCase();
  const [picked, setPicked] = useState(() => {
    const m = {};
    (position.members || []).forEach((x) => { m[x.email] = x.access; });
    return m;
  });
  const [filter, setFilter] = useState('');
  const [saving, setSaving] = useState(false);

  const list = useMemo(() => (users || [])
    .filter((u) => u.email !== owner)
    .filter((u) => !filter.trim() || `${u.name} ${u.email}`.toLowerCase().includes(filter.trim().toLowerCase()))
    .sort((a, b) => Number(!!picked[b.email]) - Number(!!picked[a.email]) || a.name.localeCompare(b.name)),
  [users, owner, filter, picked]);

  const toggle = (email) => setPicked((p) => {
    const next = { ...p };
    if (next[email]) delete next[email]; else next[email] = defaultAccess;
    return next;
  });

  const save = async () => {
    setSaving(true);
    try {
      const body = Object.entries(picked).map(([email, access]) => ({ email, access }));
      await api.put(`/positions/${position.positionId}/members`, body);
      toast.success('Assignments saved');
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(permissionError(err, 'Could not save assignments'));
    } finally {
      setSaving(false);
    }
  };

  const count = Object.keys(picked).length;
  return (
    <Modal
      title={`Assign people · ${position.positionName || ''}`}
      subtitle={`Editors can work on this position (JD, resumes, ranking, WhatsApp). Viewers can only look. New people start as ${defaultAccess === 'VIEWER' ? 'Viewer' : 'Editor'}.`}
      onClose={onClose}
      footer={(
        <>
          <span style={{ marginRight: 'auto', fontSize: 12, color: 'var(--ds-muted, #6B7280)', alignSelf: 'center' }}>{count} assigned</span>
          <button type="button" style={btn(false)} onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" style={btn(true)} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </>
      )}
    >
      <input
        placeholder="Search people…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid var(--ds-border2, #D1D5DB)', fontSize: 12, marginBottom: 8, background: 'var(--ds-input-bg, #fff)', color: 'var(--ds-text, #111827)' }}
      />
      {users === null && <div style={{ padding: 16, fontSize: 12, color: 'var(--ds-muted)' }}>Loading people…</div>}
      {users && list.length === 0 && <div style={{ padding: 16, fontSize: 12, color: 'var(--ds-muted)' }}>No one matches.</div>}
      {list.map((u) => {
        const access = picked[u.email];
        return (
          <div key={u.email} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 4px', borderBottom: '1px solid var(--ds-border, #F3F4F6)' }}>
            <input type="checkbox" checked={!!access} onChange={() => toggle(u.email)} aria-label={`Assign ${u.name}`} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600 }}>{u.name}</div>
              <div style={{ fontSize: 11, color: 'var(--ds-muted, #6B7280)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {u.email}{Array.isArray(u.roles) && u.roles.length ? ` · ${u.roles.map((r) => ROLE_SHORT[r] || r).join(' + ')}` : ''}
              </div>
            </div>
            <select
              value={access || defaultAccess}
              disabled={!access}
              onChange={(e) => setPicked((p) => ({ ...p, [u.email]: e.target.value }))}
              style={{ padding: '5px 8px', borderRadius: 7, border: '1px solid var(--ds-border2, #D1D5DB)', fontSize: 12, background: 'var(--ds-input-bg, #fff)', color: 'var(--ds-text, #111827)' }}
            >
              <option value="EDITOR">Editor</option>
              <option value="VIEWER">Viewer</option>
            </select>
          </div>
        );
      })}
    </Modal>
  );
}

/** Make someone else the owner of a position. */
export function TransferPositionModal({ position, onClose, onSaved }) {
  const users = useCompanyUsers();
  const owner = (position.createdByEmail || '').toLowerCase();
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!email) return;
    setSaving(true);
    try {
      await api.post(`/positions/${position.positionId}/transfer`, { email });
      toast.success('Ownership transferred');
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(permissionError(err, 'Could not transfer ownership'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Transfer ownership · ${position.positionName || ''}`}
      subtitle={`The new owner gets full control of this position. Current owner: ${owner || 'unknown'}.`}
      onClose={onClose}
      footer={(
        <>
          <button type="button" style={btn(false)} onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" style={btn(true)} onClick={save} disabled={saving || !email}>{saving ? 'Saving…' : 'Transfer'}</button>
        </>
      )}
    >
      <select
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--ds-border2, #D1D5DB)', fontSize: 12, marginBottom: 8, background: 'var(--ds-input-bg, #fff)', color: 'var(--ds-text, #111827)' }}
      >
        <option value="">{users === null ? 'Loading…' : 'Choose the new owner…'}</option>
        {(users || []).filter((u) => u.email !== owner).map((u) => (
          <option key={u.email} value={u.email}>{u.name} ({u.email})</option>
        ))}
      </select>
    </Modal>
  );
}
